import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { addDays, addWeeks, format, subSeconds } from 'date-fns';
import keyBy from 'lodash.keyby';
import { useMedia } from 'react-use';
import { twMerge } from 'tailwind-merge';

import {
  CalendarApi,
  DateSelectArg,
  DatesSetArg,
  EventChangeArg,
  EventClickArg,
  EventContentArg,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import { api } from '../../lib/api';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import Dialog from '../shared/Dialog';
import { showToast } from '../shared/Toast';
import { CalendarToolbar } from './CalendarToolbar';
import EventContent from './EventContent';
import EventDetails from './EventDetails';
import { EventForm } from './EventForm';
import {
  eventWithCategories,
  filterByCalendarCategories,
  formatWhenDragUpdate,
  convertEventTimeToUserTimezone,
  convertUserTimeToEventTimezone,
  ensureEventTimezones,
  getUserTimeZone,
} from './eventUtils';
import { MobileSettingsSideBar, SettingsSideBar } from './SettingsSideBar';

export interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  location: string;
  calendarCategoryId: number;
  start: Date;
  end: Date;
  participants: string[];
  color?: string;
  allDay: boolean;
  organizerEmail: string;
  sceneCardId: string;
  timezone?: string;
  displayTimezone?: string;
  originalTimezone?: string;
}

export type ProjectRole =
  | 'stunt_office_coordinator'
  | 'coordinator_admin'
  | 'coordinator'
  | 'super_coordinator'
  | 'performer'
  | 'hair_and_makeup'
  | 'wardrobe'
  | 'production'
  | 'production_manager'
  | 'assistant_director';

export interface CalendarProps {
  projectId: string;
  initialEvents: CalendarEvent[];
  calendarCategories: CalendarCategory[];
  currentUser: CurrentUser;
  isProjectAdmin: boolean;
  projectRole: ProjectRole;
  urlSelectedEventId: string;
  urlSelectedEventDate: string;
}

export interface CalendarCategory {
  id: number;
  calendar_category_group: string;
  color: string;
  name: string;
}

export interface CurrentUser {
  id: number;
  email: string;
}

export interface ProjectMember {
  id: number;
  name: string;
  email: string;
  role: string;
  imageUrl: string;
}

export interface SceneCard {
  uuid: string;
  scene_name: string;
  scene: string;
}

export default function Calendar({
  projectId,
  initialEvents,
  calendarCategories,
  currentUser,
  isProjectAdmin,
  projectRole,
  urlSelectedEventId,
  urlSelectedEventDate,
}: CalendarProps) {
  const calendarWrapperRef = useRef(null);

  const userTimezone = useMemo(() => getUserTimeZone(), []);

  const defaultSelectedCalendarCategories = calendarCategories.map((category) => category.id);

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent>(null);
  const [selectedCalendarCategories, setSelectedCalendarCategories] = useState(
    defaultSelectedCalendarCategories
  );
  const [selectedDate, setSelectedDate] = useState({
    start: null,
    end: null,
  });
  const [allDaySelected, setAllDaySelected] = useState(false);
  const [mode, setMode] = useState('view');
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(true);
  const [isMobileSettingsOpen, setIsMobileSettingsOpen] = useState(false);
  const [isEventInfoOpen, setIsEventInfoOpen] = useState(false);
  const [showConfirmationModal, setShowConfirmationDialog] = useState(false);
  const [confirmDialogActions, setConfirmDialogActions] = useState(null);
  const calendarRef = useRef(null);
  const [projectSceneCards, setProjectSceneCards] = useState<SceneCard[]>([]);
  const [isFetchingEvents, setIsFetchingEvents] = useState(false);

  const projectMembersByEmail = useMemo(() => keyBy(projectMembers, 'email'), [projectMembers]);
  const [rawEvents, setRawEvents] = useState(() => {
    const processedEvents = ensureEventTimezones(initialEvents, calendarCategories, userTimezone);
    return processedEvents.map(event => {
      if (!event.allDay) {
        return convertEventTimeToUserTimezone(event, userTimezone);
      }
      return {
        ...event,
        start: event.start instanceof Date ? event.start : new Date(event.start),
        end: event.end instanceof Date ? event.end : new Date(event.end),
      };
    });
  });

  const [events, setEvents] = useState(() => {
    return rawEvents.map(event => {
      if (!event.allDay) return event;
      
      const adjustedEnd = new Date(event.end);
      adjustedEnd.setDate(adjustedEnd.getDate() + 1);
      
      return {
        ...event,
        end: adjustedEnd,
      };
    });
  });
  const eventsById = useMemo(() => keyBy(rawEvents, 'id'), [rawEvents]);
  const calendarCategoriesById = useMemo(
    () => keyBy(calendarCategories, 'id'),
    [calendarCategories]
  );
  const allowCreateEvents = useMemo(() => {
    const allowedRoles: ProjectRole[] = [
      'production_manager',
      'coordinator',
      'stunt_office_coordinator',
      'wardrobe',
      'hair_and_makeup',
      'assistant_director',
    ];
    return isProjectAdmin || allowedRoles.includes(projectRole);
  }, [isProjectAdmin, projectRole]);

  const isMobileScreen = useMedia('(max-width: 1024px)', false);

  const initialView = 'dayGridMonth';

  const getCurrentView = useCallback(() => {
    if (!calendarRef?.current) return null;
    const calendarApi: CalendarApi = calendarRef.current.getApi();
    return calendarApi.view.type;
  }, []);

  const triggerCalendarResize = useCallback(() => {
    if (!calendarRef?.current) return null;
    const calendarApi: CalendarApi = calendarRef.current.getApi();
    calendarApi.updateSize();
  }, []);

  const unselectHighlight = useCallback(() => {
    if (!calendarRef?.current) return null;
    const calendarApi: CalendarApi = calendarRef.current.getApi();
    calendarApi.unselect();
  }, []);

  const goToToday = useCallback(() => {
    if (!calendarRef?.current) return null;
    const calendarApi: CalendarApi = calendarRef.current.getApi();
    calendarApi.today();
  }, []);

  const goToNext = useCallback(() => {
    if (!calendarRef?.current) return null;
    const calendarApi: CalendarApi = calendarRef.current.getApi();
    calendarApi.next();
  }, []);

  const goToPrevious = useCallback(() => {
    if (!calendarRef?.current) return null;
    const calendarApi: CalendarApi = calendarRef.current.getApi();
    calendarApi.prev();
  }, []);

  const goToDate = useCallback((date) => {
    if (!calendarRef?.current) return null;
    const calendarApi: CalendarApi = calendarRef.current.getApi();
    return calendarApi.gotoDate(date);
  }, []);

  const changeCalendarView = useCallback(
    (view: string) => {
      if (!calendarRef?.current || !view) return null;
      const calendarApi: CalendarApi = calendarRef.current.getApi();
      calendarApi.changeView(view);
      if (selectedDate?.start) {
        return calendarApi.gotoDate(selectedDate.start);
      }
      if (selectedEvent) {
        return calendarApi.gotoDate(selectedEvent.start);
      }
    },
    [selectedDate, selectedEvent]
  );

  const getTitle = useCallback(() => {
    if (!calendarRef?.current) return null;
    const calendarApi: CalendarApi = calendarRef.current.getApi();
    const activeStartDate = calendarApi.view.activeStart;
    const isDayView = calendarApi.view.type === 'timeGridDay';
    const isMonthView = calendarApi.view.type === 'dayGridMonth';

    if (isDayView) {
      return format(activeStartDate, 'MMM do yyyy');
    }

    if (isMonthView) {
      return isMobileScreen
        ? format(addWeeks(activeStartDate, 1), 'MMM yyyy')
        : format(addWeeks(activeStartDate, 1), 'MMMM yyyy');
    }

    return isMobileScreen
      ? format(activeStartDate, 'MMM yyyy')
      : format(activeStartDate, 'MMMM yyyy');
  }, [isMobileScreen]);

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    const { start, view, allDay } = selectInfo;
    const monthlyClick = view.type === 'dayGridMonth';

    if (mode === 'view') {
      setSelectedEvent(null);
    }

    let end = selectInfo.end;
    if (monthlyClick) {
      start.setHours(8);
      end = new Date(selectInfo.start);
      end.setHours(9);
      //   setSelectedDate({
      //     start,
      //     end: subSeconds(end, 1),
      //   });
      // setAllDaySelected(false);
      // return setIsEventInfoOpen(true);
    }

    setAllDaySelected(false);
    setSelectedDate({
      start: start,
      end: end,
    });

    setIsEventInfoOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const eventId = clickInfo.event.id;
    const event = eventsById[eventId];
    unselectHighlight();
    setMode('view');
    setSelectedEvent(event);
    setSelectedDate(null);
    setIsEventInfoOpen(true);
  };

  const renderEventContent = (eventInfo: EventContentArg) => {
    return <EventContent eventInfo={eventInfo} />;
  };

  const handleDateChange = (dateInfo: DatesSetArg) => {
    async function fetchEvents() {
      setIsFetchingEvents(true);
      try {
        const response = await api.get(
          `projects/${projectId}/calendar/fetch_events?starts_after=${dateInfo.startStr}`
        );
        const eventList = response?.data;
        if (eventList) {
          const processedEvents = ensureEventTimezones(eventList, calendarCategories, userTimezone);
          const rawConverted = processedEvents.map(event => {
            if (!event.allDay) {
              return convertEventTimeToUserTimezone(event, userTimezone);
            }
            return {
              ...event,
              start: event.start instanceof Date ? event.start : new Date(event.start),
              end: event.end instanceof Date ? event.end : new Date(event.end),
            };
          });
          
          setRawEvents(rawConverted);
          
          const adjustedEvents = rawConverted.map(event => {
            if (!event.allDay) return event;
            
            const adjustedEnd = new Date(event.end);
            adjustedEnd.setDate(adjustedEnd.getDate() + 1);
            
            return {
              ...event,
              end: adjustedEnd,
            };
          });
          setEvents(adjustedEvents);
        }
      } catch (error) {
        showToast('Error fetching events', 'danger');
        console.error('Error fetching events:', error);
      } finally {
        setIsFetchingEvents(false);
      }
    }
    fetchEvents();
  };

  const handleEventDragChange = (changeInfo: EventChangeArg) => {
    async function updateEvent() {
      try {
        const { start, end, allDay } = changeInfo.event;
        const eventData = eventsById[changeInfo.event.id];
        const eventTimezone = eventData?.originalTimezone || eventData?.timezone || userTimezone;

        // Check if this is the currently selected event
        const isSelectedEvent = selectedEvent?.id === changeInfo.event.id;

        let when;
        let timezoneParam = undefined;

        if (!allDay) {
          if (eventTimezone && eventTimezone !== userTimezone) {
            const adjustedStart = convertUserTimeToEventTimezone(start, userTimezone, eventTimezone);
            const adjustedEnd = convertUserTimeToEventTimezone(end, userTimezone, eventTimezone);

            when = formatWhenDragUpdate({
              start: adjustedStart,
              end: adjustedEnd,
              allDay
            });
          } else {
            when = formatWhenDragUpdate({ start, end, allDay });
          }
          timezoneParam = eventTimezone;
        } else {
          when = formatWhenDragUpdate({ start, end, allDay });
        }

        const result = await api.put(`projects/${projectId}/calendar/update_event`, {
          event_input: {
            event_id: changeInfo.event.id,
            when,
            timezone: timezoneParam,
          },
        });

        const newEvent = result?.data;
        if (newEvent) {
          const processedEvent = ensureEventTimezones([newEvent], calendarCategories, userTimezone)[0];
          const convertedEvent = newEvent.allDay ? processedEvent : convertEventTimeToUserTimezone(processedEvent, userTimezone);

          // Update rawEvents to keep eventsById in sync
          setRawEvents((current) => {
            const eventsWithoutUpdatedEvent = current.filter(
              (event) => event.id !== changeInfo.event.id
            );
            return [...eventsWithoutUpdatedEvent, convertedEvent];
          });

          // Update events for display (with FullCalendar adjustment for all-day events)
          setEvents((current) => {
            const eventsWithoutUpdatedEvent = current.filter(
              (event) => event.id !== changeInfo.event.id
            );
            if (convertedEvent.allDay) {
              const adjustedEnd = new Date(convertedEvent.end);
              adjustedEnd.setDate(adjustedEnd.getDate() + 1);
              return [...eventsWithoutUpdatedEvent, { ...convertedEvent, end: adjustedEnd }];
            }
            return [...eventsWithoutUpdatedEvent, convertedEvent];
          });

          // If this was the selected event being viewed, update it to reflect the new data
          if (isSelectedEvent) {
            setSelectedEvent(convertedEvent);
          }

          setMode('view');
          showToast('Event updated successfully', 'success');
        }
      } catch (error) {
        changeInfo.revert();
        showToast('Error updating event', 'danger');
        console.error('Error updating event:', error);
      }
    }
    updateEvent();
  };

  useEffect(() => {
    if (selectedEvent) return;
    if (urlSelectedEventId && urlSelectedEventDate) {
      const event = eventsById[urlSelectedEventId];
      if (event) {
        setSelectedEvent(event);
        setMode('view');
        setIsEventInfoOpen(true);
        const { start } = event;
        goToDate(start);
      }
    }
  }, [urlSelectedEventDate, urlSelectedEventId, eventsById, goToDate]);

  useEffect(() => {
    const view = getCurrentView();
    if (selectedDate && view === 'dayGridMonth') {
      const { start, end } = selectedDate;
      start?.setHours(8, 0, 0, 0);
      end?.setHours(10, 0, 0, 0);

      if (!allDaySelected) {
        setSelectedDate({
          start: start,
          end: end,
        });
      }
    }
  }, [allDaySelected]);

  const filteredEvents = useMemo(() => {
    return filterByCalendarCategories(events, selectedCalendarCategories);
  }, [events, selectedCalendarCategories]);

  useEffect(() => {
    async function fetchProjectMembers() {
      try {
        const response = await api.get(`/projects/${projectId}/participants`);
        const { users } = response.data;

        setProjectMembers(users);
      } catch (error) {
        showToast('Error while fetching participants.', 'danger');
      }
    }
    fetchProjectMembers();
  }, [projectId]);

  useEffect(() => {
    const observer = new ResizeObserver(triggerCalendarResize);

    if (calendarWrapperRef?.current) {
      observer.observe(calendarWrapperRef.current);
    }

    return () => {
      if (calendarWrapperRef?.current) {
        observer.unobserve(calendarWrapperRef.current);
      }
    };
  }, []);

  const onSettingsButtonClick = () => {
    if (isMobileScreen) {
      setIsMobileSettingsOpen(true);
    } else {
      setIsSettingsOpen(!isSettingsOpen);
    }
  };

  function handleAllDayEvents(eventData: any) {
    if (!eventData || !eventData.event) {
      return eventData;
    }
    
    const event = eventData.event;
    
    if (!event.allDay) {
      return eventData;
    }
    
    try {
      return eventData;
    } catch (error) {
      console.error('Error in handleAllDayEvents:', error);
      return eventData;
    }
  }

  useEffect(() => {
    async function fetchProjectSceneCards() {
      try {
        const response = await api.get(`/projects/${projectId}/scene_cards`);
        if (response.status !== 200) throw new Error('Error while fetching scene cards.');
        setProjectSceneCards(response.data.scene_cards);
      } catch (error) {
        showToast('Error while fetching scene cards.', 'danger');
      }
    }
    fetchProjectSceneCards();
  }, [projectId]);

  const sceneCardsById = useMemo(() => keyBy(projectSceneCards, 'uuid'), [projectSceneCards]);

  const handleSceneCardRedirect = useCallback(
    async (sceneCardId) => {
      const projectFolderId = sceneCardsById[sceneCardId]?.project_folder?.uuid;
      const { Turbo } = await import('@hotwired/turbo-rails');
      Turbo.visit(
        `/projects/${projectId}/project_folders/${projectFolderId}/scene_cards/${sceneCardId}`
      );
    },
    [projectId, selectedEvent, sceneCardsById]
  );

  return (
    <div className="flex gap-2 border-t w-full overflow-hidden" style={{ height: 'calc(100vh - 68px)' }}>
      <ConfirmationDialog
        title="Are you sure?"
        description="Changing this event will affect all participants."
        onCancelLabel="Cancel"
        onCancelAction={confirmDialogActions?.onCancel}
        onConfirmLabel="Confirm"
        onConfirmAction={confirmDialogActions?.onConfirm}
        isOpen={showConfirmationModal}
        onClose={confirmDialogActions?.onCancel}
        icon={
          <div className="rounded-full p-3 bg-red-200">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-500 " />
          </div>
        }
      />
      <SettingsSideBar
        className={twMerge(
          'hidden lg:flex flex-col bg-white shadow-sm w-72 overflow-y-auto flex-shrink-0',
          'transition-all duration-300 ease-in-out',
          isSettingsOpen ? 'w-72 opacity-100' : 'w-0 opacity-0'
        )}
        projectId={projectId}
        calendarCategories={calendarCategories}
        selectedCalendarCategories={selectedCalendarCategories}
        setSelectedCalendarCategories={setSelectedCalendarCategories}
        isProjectAdmin={isProjectAdmin}
      />
      <div ref={calendarWrapperRef} className="p-1 sm:p-2 w-full min-w-0 flex flex-col">
        <CalendarToolbar
          goToNext={goToNext}
          goToPrevious={goToPrevious}
          goToToday={goToToday}
          onSettingsButtonClick={onSettingsButtonClick}
          getTitle={getTitle}
          changeCalendarView={changeCalendarView}
          currentView={getCurrentView()}
        />
        <div className={twMerge('w-full flex-1 min-h-0 relative')}>
          {isFetchingEvents && (
            <div className="absolute top-0 left-0 right-0 z-50">
              <div className="h-1 w-full bg-blue-100 overflow-hidden">
                <div className="h-full bg-blue-600 animate-progress origin-left" />
              </div>
            </div>
          )}
          <FullCalendar
            eventClassNames={(eventInfo) => {
              const isSelected = selectedEvent?.id === eventInfo.event.id;
              return isSelected ? 'selected-event' : '';
            }}
            ref={calendarRef}
            slotLabelClassNames={['text-xs sm:text-sm text-gray-500']}
            viewClassNames={['w-full text-xs sm:text-sm text-gray-500 bg-white']}
            allDayText="All Day"
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            titleFormat={{ year: 'numeric', month: 'long' }}
            nowIndicatorClassNames={['w-full']}
            headerToolbar={false}
            expandRows={true}
            slotDuration={'00:30:00'}
            eventMinHeight={isMobileScreen ? 35 : 30}
            height="100%"
            initialView={initialView}
            editable={isProjectAdmin || projectRole === 'production_manager'}
            views={{
              week: {
                dayHeaderFormat: { day: '2-digit', weekday: 'short' },
              },
              day: {
                dayHeaderFormat: { day: '2-digit', weekday: 'short' },
              },
            }}
            longPressDelay={isMobileScreen ? 300 : 500}
            selectMirror={true}
            selectable={allowCreateEvents}
            selectMinDistance={isMobileScreen ? 5 : 0}
            scrollTime={'06:00:00'}
            dayMaxEvents={isMobileScreen ? 3 : true}
            nowIndicator={true}
            weekends={true}
            unselectAuto={false}
            events={filteredEvents?.filter(Boolean)}
            eventChange={handleEventDragChange}
            eventContent={renderEventContent}
            eventClick={handleEventClick}
            datesSet={handleDateChange}
            select={handleDateSelect}
            windowResizeDelay={300}
            eventDataTransform={handleAllDayEvents}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            dayHeaderClassNames={['text-xs sm:text-sm']}
            dayCellClassNames={['touch-manipulation']}
          />
        </div>
      </div>

      <div
        className={twMerge(
          'hidden lg:flex shadow-sm bg-white w-72 flex-shrink-0 overflow-y-auto',
          'transition-all duration-300 ease-in-out',
          isEventInfoOpen ? 'w-72 opacity-100' : 'w-0 opacity-0'
        )}
      >
        {selectedEvent && mode === 'view' ? (
          <EventDetails
            selectedEvent={selectedEvent}
            calendarCategoriesById={calendarCategoriesById}
            setMode={setMode}
            projectMembersByEmail={projectMembersByEmail}
            setIsEventInfoOpen={setIsEventInfoOpen}
            isProjectAdmin={isProjectAdmin}
            projectRole={projectRole}
            sceneCard={sceneCardsById[selectedEvent?.sceneCardId]}
            handleSceneCardRedirect={handleSceneCardRedirect}
          />
        ) : (
          <EventForm
            projectId={projectId}
            projectMembers={projectMembers}
            projectMembersByEmail={projectMembersByEmail}
            projectSceneCards={projectSceneCards}
            currentUser={currentUser}
            selectedEvent={selectedEvent}
            setSelectedEvent={setSelectedEvent}
            setEvents={setEvents}
            setRawEvents={setRawEvents}
            selectedDate={selectedDate}
            calendarCategories={calendarCategories}
            setMode={setMode}
            setIsEventInfoOpen={setIsEventInfoOpen}
            setAllDaySelected={setAllDaySelected}
            allDaySelected={allDaySelected}
            projectRole={projectRole}
          />
        )}
      </div>
      {isMobileScreen && (
        <>
          <Dialog open={isMobileSettingsOpen} onClose={() => setIsMobileSettingsOpen(false)}>
            <MobileSettingsSideBar
              currentView={getCurrentView()}
              projectId={projectId}
              calendarCategories={calendarCategories}
              selectedCalendarCategories={selectedCalendarCategories}
              setSelectedCalendarCategories={setSelectedCalendarCategories}
              changeCalendarView={changeCalendarView}
              initialView={initialView}
              setIsSettingsOpen={setIsMobileSettingsOpen}
              isProjectAdmin={isProjectAdmin}
            />
          </Dialog>
          <Dialog open={isEventInfoOpen} onClose={() => setIsEventInfoOpen(false)}>
            {selectedEvent && mode === 'view' ? (
              <div style={{ height: 'calc(85vh - 40px)', maxHeight: '700px' }}>
                <EventDetails
                  sceneCard={sceneCardsById[selectedEvent?.sceneCardId]}
                  selectedEvent={selectedEvent}
                  calendarCategoriesById={calendarCategoriesById}
                  setMode={setMode}
                  projectMembersByEmail={projectMembersByEmail}
                  setIsEventInfoOpen={setIsEventInfoOpen}
                  isProjectAdmin={isProjectAdmin}
                  projectRole={projectRole}
                  handleSceneCardRedirect={handleSceneCardRedirect}
                />
              </div>
            ) : (
              <div style={{ height: 'calc(85vh - 40px)', maxHeight: '700px' }}>
                <EventForm
                  projectId={projectId}
                  projectMembers={projectMembers}
                  projectMembersByEmail={projectMembersByEmail}
                  projectSceneCards={projectSceneCards}
                  currentUser={currentUser}
                  selectedEvent={selectedEvent}
                  setSelectedEvent={setSelectedEvent}
                  setEvents={setEvents}
                  setRawEvents={setRawEvents}
                  selectedDate={selectedDate}
                  calendarCategories={calendarCategories}
                  setMode={setMode}
                  setIsEventInfoOpen={setIsEventInfoOpen}
                  setAllDaySelected={setAllDaySelected}
                  allDaySelected={allDaySelected}
                  projectRole={projectRole}
                />
              </div>
            )}
          </Dialog>
        </>
      )}
    </div>
  );
}